import { AxiosResponse, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import queryString from 'query-string';

import { isFilenameImage, uderlyConfig } from "..";
import Settings from "../data/settings";

export const delay = async (timeMs: number) => await new Promise(r => setTimeout(r, timeMs));

export const setAxiosInterceptors = (accessToken: string | null = null, language: string | null = null) => {
  const onFullfilled = (config: InternalAxiosRequestConfig<any>) => {
    config.headers.Authorization = `Bearer ${accessToken}`;

    if (language)
      config.headers['Accept-Language'] = language;

    return config;
  }

  const onRejected = (error: any) => Promise.reject(error);

  uderlyConfig.Axios?.interceptors.request.use(onFullfilled, onRejected, { synchronous: true });

  // uderlyConfig.Axios?.interceptors.response.use((response) => {
  //   return response;
  // }, (error) => {
  //   if (error && error.response && error.response.status === 401) {
  //     console.error("401 UNAUTHORIZED - clearAxiosInterceptors", error.response);
  //   //   clearAxiosInterceptors();
  //   }
  //   return error.response;
  // });
}

export const clearAxiosInterceptors = () => {
  uderlyConfig.Axios?.interceptors.request.clear();
}

export const mapData = (data: any, fieldToMap?: string | null) => {
  if (fieldToMap && fieldToMap.length > 0) {
    const mappedData = [];
    for (const x of data) {
      mappedData[x[fieldToMap]] = x;
    }
    return mappedData;
  }
  return data;
}

export const errorLog = (error: any) => {
  if (error.response) {
    // Request made and server responded
    console.log(error.response);
    console.log(error.response.data);
    console.log(error.response.status);
    console.log(error.response.headers);
  } else if (error.request) {
    // The request was made but no response was received
    console.log(error.request);
  } else {
    // Something happened in setting up the request that triggered an Error
    console.log('Error', error.message);
  }

  return (error.response) ? error.response : null;
}

export const getItemRequest = async (url: string, cancelToken?: any): Promise<void | AxiosResponse<any>> => {
  const config: AxiosRequestConfig = {};

  if (cancelToken)
    config.cancelToken = cancelToken;

  return await uderlyConfig.Axios?.get(url, config)
    .catch(function (error) {
      if (uderlyConfig.Axios?.isCancel(error)) {
        console.log('Request canceled', error.message);
      } else {
        console.log('error ' + error);
      }

      errorLog(error);
    });
}

export const createItemRequest = async (url: string, values: any, config?: AxiosRequestConfig): Promise<void | AxiosResponse<any>> => {
  console.log("POST", url, values);

  return await uderlyConfig.Axios?.post(url, values, config)
    .catch(function (error) {
      console.log('error ' + error);

      errorLog(error);

      return error.response;
    });
}

export const updateItemRequest = async (url: string, values: any, config?: AxiosRequestConfig): Promise<void | AxiosResponse<any>> => {
  console.log("UPDATE", url, values);

  return await uderlyConfig.Axios?.put(url, values, config)
    .catch(function (error) {
      console.log('error ' + error);

      errorLog(error);

      return error.response;
    });
}

export const deleteItemRequest = async (url: string, config?: AxiosRequestConfig): Promise<void | AxiosResponse<any>> => {
  console.log("DELETE", url);

  return await uderlyConfig.Axios?.delete(url, config)
    .catch(function (error) {
      console.log('error ' + error);

      errorLog(error);

      return error.response;
    });
}

export const getRecordsRequest = async (url: string, filters: object | null | undefined = null,
  abortController: AbortController | undefined = undefined): Promise<void | AxiosResponse<any>> => {
  if (filters) {
    url += ('?' + queryString.stringify(filters));
  }

  return await uderlyConfig.Axios?.get(url, !abortController ? {} : {
    signal: abortController.signal
  }).catch(function (error) {
    if (uderlyConfig.Axios?.isCancel(error)) {
      console.log('Request canceled', error.message);
    } else {
      console.log('error ' + error);
    }

    errorLog(error);

    return error.message;
  });
}

export const uploadFile = async (url: string, formData: FormData) => {
  console.log("UPLOAD", url, formData);
  const config = {
    headers: {
      'Content-Type': "multipart/form-data"
    }
  };

  return await uderlyConfig.Axios?.post(url, formData, config)
    .catch((error) => {
      console.log(error);
      errorLog(error);

      return error.response;
    });
}

export const mediaUrl = (mediaStockId: number, fileName: string, version?: string | undefined) =>
  entityMediaUrl(mediaStockId, "mediastock", fileName, version);

export const entityMediaUrl = (itemId: number, path: string, fileName: string, version?: string | undefined) => {
  let url = `${Settings.apiUrl}/${path}/${itemId}/media/file?name=${fileName}`;

  if (isFilenameImage(fileName) && version)
    url += `&version=${version ?? 'default'}`;

  return url;
}

export interface ImageInfo {
  exists: boolean;
  width?: number;
  height?: number;
  error?: string;
  imageElement?: HTMLImageElement;
}

export const getImageInfo = (imageUrl: string): Promise<ImageInfo> => {
  return new Promise((resolve) => {
    const img = new Image();

    // Optional: Add cross-origin attribute if loading from different domains
    img.crossOrigin = "anonymous";

    img.onload = () => {
      resolve({
        exists: true,
        width: img.naturalWidth,
        height: img.naturalHeight,
        imageElement: img
      });
    };

    img.onerror = () => {
      resolve({
        exists: false,
        error: 'Image failed to load'
      });
    };

    // Add a timeout to handle cases where the image request might hang
    const timeoutDuration = 5000; // 5 seconds timeout
    const timeout = setTimeout(() => {
      resolve({
        exists: false,
        error: 'Request timed out'
      });
    }, timeoutDuration);

    img.src = imageUrl;

    // Clean up timeout if image loads or errors out before timeout
    img.onload = () => {
      clearTimeout(timeout);
      resolve({
        exists: true,
        width: img.naturalWidth,
        height: img.naturalHeight,
        imageElement: img
      });
    };

    img.onerror = () => {
      clearTimeout(timeout);
      resolve({
        exists: false,
        error: 'Image failed to load'
      });
    };
  });
};